import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { DollarSign, TrendingUp, TrendingDown, AlertCircle, Wallet } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { businessToday } from '@/lib/format';
import { fetchLandingTransactions, summariseLandingTransactions } from '@/lib/landingAnalytics';
import { cn } from '@/lib/utils';

export default function Analytics() {
  const [monthRange, setMonthRange] = useState({
    from: startOfMonth(subMonths(businessToday(), 11)),
    to: endOfMonth(businessToday()),
  });

  const formatDateForQuery = (date: Date) => format(date, 'yyyy-MM-dd');

  const startDateStr = formatDateForQuery(monthRange.from);
  const endDateStr = formatDateForQuery(addMonths(startOfMonth(monthRange.to), 1));

  // Every transaction in the range, read in pages: one request stops at PostgREST's Max rows
  // (1,000) without an error, and the default year holds about seven times that. The read, its
  // `(transaction_at, id)` order and its count check are in `src/lib/landingAnalytics.ts`.
  const { data: transactions, isLoading: transactionsLoading, error: transactionsError } = useQuery({
    queryKey: ['analytics-transactions', startDateStr, endDateStr],
    queryFn: () => fetchLandingTransactions(startDateStr, endDateStr),
  });

  // Calculate stats from transactions
  const { stats, monthlyTrend, revenueBreakdown, expenseBreakdown, otherCurrencies } = useMemo(
    () => summariseLandingTransactions(transactions),
    [transactions],
  );

  const formatCurrency = (amount: number, currency: string = 'GHS') => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount);
  };

  const pieColors = [
    'hsl(215, 25%, 45%)',
    'hsl(160, 25%, 45%)',
    'hsl(35, 30%, 50%)',
    'hsl(280, 20%, 50%)',
    'hsl(190, 25%, 45%)',
    'hsl(350, 25%, 50%)',
    'hsl(90, 20%, 45%)',
    'hsl(25, 30%, 50%)',
  ];

  const header = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">Financial performance and trends</p>
      </div>
      <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
    </div>
  );

  // A read that failed, or came back short, must not render as money: with no rows the cards would
  // print GHS 0.00 as the period's figures, the breakdowns would say there was no revenue, and the
  // trend chart would stay a skeleton.
  if (transactionsError) {
    return (
      <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
        {header}
        <p className="py-8 text-center text-sm text-muted-foreground">
          Transactions could not be loaded: {(transactionsError as Error).message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {header}

      {/* Financial Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {transactionsLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(stats?.periodRevenue || 0)}</div>
                {otherCurrencies.filter(o => o.revenue !== 0).map(o => (
                  <p key={o.currency} className="text-xs text-muted-foreground mt-1">and {formatCurrency(o.revenue, o.currency)}</p>
                ))}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Expenses</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {transactionsLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(stats?.periodExpenses || 0)}</div>
                {otherCurrencies.filter(o => o.expenses !== 0).map(o => (
                  <p key={o.currency} className="text-xs text-muted-foreground mt-1">and {formatCurrency(o.expenses, o.currency)}</p>
                ))}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Net Income</CardTitle>
            {(stats?.netIncome || 0) >= 0 ? <TrendingUp className="h-4 w-4 text-muted-foreground" /> : <TrendingDown className="h-4 w-4 text-muted-foreground" />}
          </CardHeader>
          <CardContent>
            {transactionsLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className={cn("text-2xl font-bold", (stats?.netIncome || 0) < 0 && "text-destructive")}>
                  {formatCurrency(stats?.netIncome || 0)}
                </div>
                {otherCurrencies.map(o => (
                  <p key={o.currency} className="text-xs text-muted-foreground mt-1">and {formatCurrency(o.netIncome, o.currency)}</p>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
          <CardDescription>Monthly performance, GHS</CardDescription>
        </CardHeader>
        <CardContent>
          {monthlyTrend && monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyTrend}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--foreground))" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--foreground))" fill="url(#revenueGrad)" strokeWidth={2} name="Revenue" />
                <Area type="monotone" dataKey="expenses" stroke="hsl(var(--muted-foreground))" fill="transparent" strokeWidth={1} strokeDasharray="4 4" name="Expenses" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton className="h-[300px] w-full" />}
        </CardContent>
      </Card>

      {/* Bottom Row */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Breakdown</CardTitle>
            <CardDescription>By category, GHS</CardDescription>
          </CardHeader>
          <CardContent>
            {!transactionsLoading && revenueBreakdown && revenueBreakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={revenueBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2} dataKey="value">
                      {revenueBreakdown.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1 mt-2">
                  {revenueBreakdown.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: pieColors[index % pieColors.length] }} />
                        <span className="text-muted-foreground truncate max-w-[150px]">{item.name}</span>
                      </div>
                      <span className="font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                {transactionsLoading ? (
                  <Skeleton className="h-40 w-40 rounded-full" />
                ) : (
                  <><AlertCircle className="mr-2 h-4 w-4" />No revenue</>
                )}
              </div>
            )}
            {otherCurrencies.filter(o => o.revenueBreakdown.length > 0).map(o => (
              <div key={o.currency} className="flex flex-col gap-1 mt-4">
                <span className="text-xs font-medium text-muted-foreground">{o.currency}, not in the chart</span>
                {o.revenueBreakdown.map(item => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate max-w-[150px]">{item.name}</span>
                    <span className="font-medium">{formatCurrency(item.value, o.currency)}</span>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Expense Breakdown</CardTitle>
            <CardDescription>By category, GHS</CardDescription>
          </CardHeader>
          <CardContent>
            {!transactionsLoading && expenseBreakdown && expenseBreakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={expenseBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2} dataKey="value">
                      {expenseBreakdown.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1 mt-2">
                  {expenseBreakdown.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: pieColors[index % pieColors.length] }} />
                        <span className="capitalize text-muted-foreground truncate max-w-[150px]">{item.name.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                {transactionsLoading ? (
                  <Skeleton className="h-40 w-40 rounded-full" />
                ) : (
                  <><AlertCircle className="mr-2 h-4 w-4" />No expenses</>
                )}
              </div>
            )}
            {otherCurrencies.filter(o => o.expenseBreakdown.length > 0).map(o => (
              <div key={o.currency} className="flex flex-col gap-1 mt-4">
                <span className="text-xs font-medium text-muted-foreground">{o.currency}, not in the chart</span>
                {o.expenseBreakdown.map(item => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-muted-foreground truncate max-w-[150px]">{item.name.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{formatCurrency(item.value, o.currency)}</span>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}